import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Package } from '../types/Package';
import { findServiceBySlug, loadServicesFromServer } from '../utils/ServiceAPI';
import { formatCurrency } from '../utils/CurrencyUtils';
import SEOHead from '../components/SEO/SEOHead';
import InquiryDatePicker from '../components/Common/InquiryDatePicker';
import { useInquiryDate, withInquiryDate } from '../utils/inquiryDate';
import { preferredDateLine } from '../utils/preferredDate';
import { LOCAL_BUSINESS, whatsappHref } from '../utils/localBusiness';
import { cloudinaryDeliveryUrl, cloudinarySrcSet } from '../utils/imageSeo';

const ServiceDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [inquiryDate] = useInquiryDate(true);
  const [service, setService] = useState<Package | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadService = async () => {
      if (!slug) {
        if (isMounted) {
          setError('No service specified');
          setLoading(false);
        }
        return;
      }

      try {
        // Load services with timeout (handled in ServiceAPI)
        const all = await loadServicesFromServer();
        const found = findServiceBySlug(all, slug);
        
        if (isMounted) {
          if (found) {
            setService(found);
            setError(null);
          } else {
            setError('Service not found');
          }
          setLoading(false);
        }
      } catch (e) {
        if (isMounted) {
          console.error('Error loading service:', e);
          setError('Failed to load service');
          setLoading(false);
        }
      }
    };

    loadService();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="container" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading service...</p>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="container" style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Service Not Found</h1>
        <p>{error || 'The requested service could not be found.'}</p>
        <button 
          onClick={() => navigate('/services')}
          className="btn btn-primary"
          style={{ marginTop: '1rem' }}
        >
          Back to Services
        </button>
      </div>
    );
  }

  const price = service.isSpecial && service.specialPrice ? service.specialPrice : service.price;
  const originalPrice = service.price;
  const hasDiscount = Boolean(service.isSpecial && service.specialPrice && service.price && service.specialPrice < service.price);
  const serviceImage = service.image || service.heroImage?.secure_url;

  return (
    <div className="container" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24, padding: '2rem' }}>
      <SEOHead
        title={`${service.name} - Photography Service`}
        description={service.description || service.short || `${service.name} photography service by Carmen Lorraine Photography`}
        image={serviceImage}
        keywords={[
          service.category || 'photography',
          service.name.toLowerCase(),
          'photography service',
          'Cape Town photography'
        ]}
        type="website"
      />
      <button 
        onClick={() => navigate('/services')}
        className="btn btn-secondary"
        style={{ justifySelf: 'start' }}
      >
        ← Back to Services
      </button>

      <h1>{service.name}</h1>
      
      {service.image && (
        <img
          src={cloudinaryDeliveryUrl(service.image, 1600) || service.image}
          srcSet={cloudinarySrcSet(service.image, [640, 960, 1400, 2000])}
          sizes="(max-width: 720px) 100vw, 960px"
          alt={service.name}
          style={{ width: '100%', maxHeight: 420, objectFit: 'cover', borderRadius: 12 }}
        />
      )}
      
      {service.short && (
        <p style={{ fontSize: 18, fontWeight: 500 }}>{service.short}</p>
      )}
      
      {price != null && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {hasDiscount && originalPrice && (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#666', marginRight: '1rem' }}>
                {formatCurrency(originalPrice, service.currency || 'R')}
              </span>
              <span style={{ color: '#d32f2f', fontWeight: 700 }}>
                Save {formatCurrency(originalPrice - price, service.currency || 'R')}
              </span>
            </div>
          )}
          <p style={{ fontWeight: 700, fontSize: '1.5rem' }}>
            {formatCurrency(price, service.currency || 'R')}
            {service.isSpecial && (
              <span style={{ marginLeft: '1rem', fontSize: '0.9rem', color: '#d32f2f' }}>
                Special Offer
              </span>
            )}
          </p>
        </div>
      )}
      
      {service.description && (
        <div style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
          {service.description}
        </div>
      )}

      {service.features && service.features.length > 0 && (
        <div>
          <h3>What's Included</h3>
          <ul style={{ lineHeight: 1.8 }}>
            {service.features.map((feature, index) => (
              <li key={index}>{feature.replace(/\*\*/g, '').replace(/\*/g, '')}</li>
            ))}
          </ul>
        </div>
      )}

      {service.durationMinutes && (
        <div>
          <strong>Duration:</strong> {Math.floor(service.durationMinutes / 60)} hour{Math.floor(service.durationMinutes / 60) !== 1 ? 's' : ''}
          {service.durationMinutes % 60 > 0 && ` ${service.durationMinutes % 60} minutes`}
        </div>
      )}

      {service.category && (
        <div>
          <strong>Category:</strong> {service.category}
        </div>
      )}

      <div style={{ marginTop: '1rem', maxWidth: 420 }}>
        <InquiryDatePicker />
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: '1rem' }}>
        <a 
          className="btn btn-primary" 
          href={whatsappHref(LOCAL_BUSINESS.telephone, withInquiryDate(`${window.location.href}\n\nHi Carmen, I'm interested in ${service.name}.`))} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          WhatsApp
        </a>
        <a 
          className="btn btn-secondary" 
          href={`mailto:hello@carmenlorraine.com?subject=Inquiry about ${encodeURIComponent(service.name)}&body=Hi Carmen,%0D%0A%0D%0AI'm interested in learning more about your ${encodeURIComponent(service.name)} service.%0D%0A%0D%0A${encodeURIComponent(preferredDateLine(inquiryDate))}%0D%0A%0D%0AThank you!`}
        >
          Enquire
        </a>
      </div>
    </div>
  );
};

export default ServiceDetailPage;
